import { Fragment, useCallback, useEffect, useState } from "react";
import { LIVE_INTERVAL_MS, useVisibleInterval } from "../lib/polling";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, Tooltip,
  FormControlLabel, Switch, Paper, TextField, Button, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fmtArDateTime } from "../services/monitoring";
import {
  getTransferencias, fmtMoney, fmtBd,
  type Transferencias, type TransferenciaRow,
} from "../services/monitoreoVivo";


const nf = new Intl.NumberFormat("es-AR");

const ESTADO_COLOR: Record<string, "success" | "info" | "warning"> = {
  Transferido: "success",
  Acreditado: "info",
  "A revisar": "warning",
};

function StatTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 170, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

/** yyyyMMdd ↔ yyyy-MM-dd (input type="date") */
const bdToInput = (bd: string) => (/^\d{8}$/.test(bd) ? `${bd.slice(0, 4)}-${bd.slice(4, 6)}-${bd.slice(6, 8)}` : "");
const inputToBd = (v: string) => v.replaceAll("-", "");

function Row({ r }: { r: TransferenciaRow }) {
  return (
    <TableRow sx={!r.cuenta_totales ? { opacity: 0.65 } : undefined}>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.vendor}</Typography>
        {r.merchant && <Typography variant="caption" color="text.secondary">{r.merchant}</Typography>}
      </TableCell>
      <TableCell>{r.group || "—"}</TableCell>
      <TableCell align="right">{r.cobros != null ? nf.format(r.cobros) : "—"}</TableCell>
      <TableCell>
        <Chip size="small" label={r.estado} color={ESTADO_COLOR[r.estado]} variant="outlined" />
        {r.es_fondeo && <Chip size="small" label="Fondeo" variant="outlined" sx={{ ml: 0.5 }} />}
      </TableCell>
      <TableCell align="right">{fmtMoney(r.net, r.currency)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(r.total, r.currency)}</TableCell>
      <TableCell align="right">{fmtMoney(r.fee, r.currency)}</TableCell>
    </TableRow>
  );
}

export default function MonitoreoTransferencias() {
  const [data, setData] = useState<Transferencias | null>(null);
  const [date, setDate] = useState<string>("");       // yyyyMMdd; "" = default backend (día hábil anterior)
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [agrupar, setAgrupar] = useState(false);
  const [pais, setPais] = useState<"ars" | "mxn">("ars");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await getTransferencias(date ? { date } : undefined);
      setData(d);
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, LIVE_INTERVAL_MS, auto);

  const shownDate = data?.date ?? date;

  // Filas del país seleccionado (por moneda: ars=Argentina, mxn=México).
  const paisRows = data?.rows.filter((r) => r.currency === pais) ?? [];

  // Agrupado client-side por collectors_group, con subtotales por moneda.
  const groups = new Map<string, TransferenciaRow[]>();
  for (const r of paisRows) {
    const k = agrupar ? (r.group || "(sin grupo)") : "";
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(r);
  }

  const totales = data?.totales[pais];

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap">
        <Box>
          <Typography variant="h5">Liquidaciones</Typography>
          <Typography variant="body2" color="text.secondary">
            Transferencias/liquidaciones por fecha de negocio de los cobros. Misma fuente que el resumen de pagos por email.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }} useFlexGap>
        <TextField
          type="date" size="small" label="Fecha de negocio"
          InputLabelProps={{ shrink: true }}
          value={bdToInput(shownDate || "")}
          onChange={(e) => setDate(inputToBd(e.target.value))}
        />
        {data && date && date !== data.business_dates.previous && (
          <Button size="small" onClick={() => setDate("")}>Volver al día hábil anterior</Button>
        )}
        <FormControlLabel
          control={<Switch checked={agrupar} onChange={(e) => setAgrupar(e.target.checked)} />}
          label="Agrupar por grupo"
        />
        <FormControlLabel
          control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={`Auto-refresh (${LIVE_INTERVAL_MS / 1000}s)`}
        />
        <Tooltip title="Refrescar ahora">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
        {updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Última actualización: {fmtArDateTime(updatedAt)}
          </Typography>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {data && (
        <Stack spacing={2}>
          <Tabs value={pais} onChange={(_e, v) => setPais(v)}>
            <Tab value="ars" label="Argentina" />
            <Tab value="mxn" label="México" />
          </Tabs>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {totales ? (
              <>
                <StatTile label="Transferido" value={fmtMoney(totales.transferido, pais)} color="#166534" />
                <StatTile label="Acreditado" value={fmtMoney(totales.acreditado, pais)} color="#1E40AF" />
                <StatTile label="A revisar" value={fmtMoney(totales.a_revisar, pais)}
                          color={totales.a_revisar > 0 ? "#B45309" : undefined} />
                <StatTile label="Comisiones" value={fmtMoney(totales.comision, pais)} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">Sin operaciones para esta fecha.</Typography>
            )}
          </Stack>

          <Alert severity="info" variant="outlined" icon={false} sx={{ py: 0.5 }}>
            <Typography variant="body2">
              <strong>Fecha de negocio:</strong> {fmtBd(data.date)}
              {"  ·  "}{nf.format(paisRows.length)} operaciones
              {"  ·  "}
              <span style={{ color: "#6B7280" }}>
                los totales solo suman recaudación (las filas atenuadas no cuentan)
              </span>
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Collector</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell align="right"># Cobros</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Neto</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Comisión</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...groups.entries()].map(([g, rows]) => {
                  const sub = new Map<string, number>();
                  for (const r of rows) {
                    if (r.cuenta_totales) sub.set(r.currency, (sub.get(r.currency) ?? 0) + r.total);
                  }
                  return (
                    <Fragment key={g || "all"}>
                      {agrupar && (
                        <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                          <TableCell colSpan={5} sx={{ fontWeight: 700 }}>{g}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {[...sub.entries()].map(([c, v]) => fmtMoney(v, c)).join(" · ") || "—"}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      )}
                      {rows.map((r, i) => <Row key={`${r.vendor}-${i}`} r={r} />)}
                    </Fragment>
                  );
                })}
                {paisRows.length === 0 && (
                  <TableRow><TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">Sin transferencias para esa fecha.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Box>
  );
}
